import JSZip from 'jszip';
import { saveAs } from 'file-saver';

type FleetResponse<T = unknown> = {
	data: T;
	metadata: null;
};

export interface ZipFile {
	name: string;
	url: string;
}

/**
 * Downloads multiple files and creates a zip archive
 * @param files Array of files with name and URL
 * @param zipName Name of the zip file (without .zip extension)
 * @returns Promise that resolves when zip is created and download starts
 */
/**
 * Sanitizes filename by removing or replacing problematic characters
 */
const sanitizeFilename = (filename: string): string => {
	// Replace characters that can cause issues in file systems
	return filename
		.replace(/[/\\:*?"<>|]/g, '_') // Replace path separators and problematic chars with underscore
		.replace(/\s+/g, ' ') // Normalize whitespace
		.replace(/^\.+/, '') // Remove leading dots
		.replace(/\.+$/, '') // Remove trailing dots
		.trim();
};

/**
 * Extracts filename from signed URL by finding the last path segment
 */
const extractFilenameFromSignedUrl = (signedUrl: string): string | null => {
	try {
		// Find the query parameters start
		const questionMarkIndex = signedUrl.indexOf('?');
		const urlPath =
			questionMarkIndex !== -1
				? signedUrl.substring(0, questionMarkIndex)
				: signedUrl;

		// Find the last path segment (filename)
		const lastSlashIndex = urlPath.lastIndexOf('/');
		if (lastSlashIndex === -1) return null;

		const filename = urlPath.substring(lastSlashIndex + 1);

		// Decode URL encoding
		const decodedFilename = decodeURIComponent(filename);

		// Validate it looks like a filename
		if (decodedFilename.includes('.') && decodedFilename.length > 0) {
			return decodedFilename;
		}

		return null;
	} catch (error) {
		return null;
	}
};

export async function createAndDownloadZip(
	files: ZipFile[],
	zipName: string = 'documents',
): Promise<void> {
	const zip = new JSZip();

	// Download all files and add them to the zip
	const downloadPromises = files.map(async (file) => {
		try {
			const response = await fetch(file.url);
			if (!response.ok) {
				throw new Error(`Failed to fetch ${file.name}: ${response.statusText}`);
			}

			const blob = await response.blob();
			// Sanitize filename to prevent file system issues
			const sanitizedName = sanitizeFilename(file.name);
			zip.file(sanitizedName, blob);
		} catch (error) {
			console.error(`Error downloading ${file.name}:`, error);
			// Add an error file to the zip to indicate the failure
			const sanitizedErrorName = sanitizeFilename(`${file.name}_ERROR.txt`);
			zip.file(
				sanitizedErrorName,
				`Failed to download file: ${file.name}\nError: ${error}`,
			);
		}
	});

	await Promise.all(downloadPromises);

	// Generate the zip file
	const zipBlob = await zip.generateAsync({ type: 'blob' });

	// Save the zip file
	const finalZipName = zipName.endsWith('.zip') ? zipName : `${zipName}.zip`;
	saveAs(zipBlob, finalZipName);
}

/**
 * Downloads multiple documents using signed URLs and creates a zip file
 * @param documentIds Array of document IDs to download
 * @param documentNames Array of document names corresponding to the IDs (optional)
 * @param downloadDocumentFn Function that takes a document ID and MPP ID and returns a promise with signed URL response
 * @param mppId MPP ID for the documents
 * @param zipName Name of the zip file
 * @returns Promise that resolves when zip is created and download starts
 */
export async function downloadDocumentsAsZip(
	documentIds: number[],
	documentNames: string[] = [],
	downloadDocumentFn: (params: {
		id: number;
		mppId: number;
	}) => Promise<FleetResponse<{ signedUrl: string }>>,
	mppId: number,
	zipName: string = 'documents',
): Promise<void> {
	// First, get all signed URLs for the documents
	const urlPromises = documentIds.map(async (id) => {
		const result = await downloadDocumentFn({ id, mppId });
		return {
			id,
			signedUrl: result?.data?.signedUrl,
		};
	});

	const urlResults = await Promise.all(urlPromises);

	// Filter out any failed downloads and create file objects
	const files: ZipFile[] = urlResults
		.filter((result) => result.signedUrl)
		.map((result) => {
			const documentIndex = documentIds.indexOf(result.id);
			const documentName =
				documentNames[documentIndex] || `document_${result.id}`;

			// Try to extract filename from signed URL
			const urlFilename = extractFilenameFromSignedUrl(result.signedUrl);
			const filename = urlFilename || documentName;

			return {
				name: filename,
				url: result.signedUrl,
			};
		});

	if (files.length === 0) {
		throw new Error('No documents could be downloaded');
	}

	await createAndDownloadZip(files, zipName);
}
